// refactor the whole junk asap

import angular from 'angular';
import { find, filter, debounce } from 'lodash';

import analytics from 'common/analytics';
import { getErrorMessage, getRepeatEndFieldError } from 'application/util/error.util';
import { toFixed } from 'application/util/number.util';
import { isCategoryTransfer, isCategoryBalanceUpdate, isCategoryOther } from 'application/util/category.util';
import {
    TRANSACTION_TYPE_INCOME,
    TRANSACTION_TYPE_EXPENSE,

    TRANSACTION_KIND_EVENT,
    TRANSACTION_KIND_TRANSFER,
    TRANSACTION_KIND_BALANCE_UPDATE
} from 'application/util/transaction.util';
import { getIncludeInReports } from 'application/util/event-report.util';
import receiptIcon from './image/icon-image.svg';

import './event-edit-modal.style.sass';
import template from './event-edit-modal.tpl.pug';

const MIN_NOTE_LENGTH_FOR_AUTOCATEGORIZATION = 2;
const MAX_NOTE_LENGTH_FOR_AUTOCATEGORIZATION = 16;
const RECEIPT_MAX_EDGE = 2048;
const RECEIPT_JPEG_QUALITY = 0.82;

class EventEditController {
    /* @ngInject */
    constructor($q, $element, $window, timeUtil, numberUtil, eventUtil, categoryService, eventService, transferService, balanceUpdateService, sessionService, suggestionService, autoCategoryService, modalService, notificationService, storageService) {
        this.$q = $q;
        this.$element = $element;
        this.$window = $window;
        this.timeUtil = timeUtil;
        this.numberUtil = numberUtil;
        this.eventUtil = eventUtil;
        this.categoryService = categoryService;
        this.eventService = eventService;
        this.transferService = transferService;
        this.balanceUpdateService = balanceUpdateService;
        this.sessionService = sessionService;
        this.autoCategoryService = autoCategoryService;
        this.suggestionService = suggestionService;
        this.modalService = modalService;
        this.notificationService = notificationService;
        this.storageService = storageService;
        this.receiptIconUrl = `url(${receiptIcon})`;
        this.handleReceiptInputChange = this.handleReceiptInputChange.bind(this);
    }

    $onInit() {
        const categoryIconStyle = this.storageService.getUserItem('category-icon-style') || 'icons';
        this.useCategoryIcons = categoryIconStyle === 'icons';
        this.categoryService.onUpdate(this.refreshCategories.bind(this));
        this.types = [TRANSACTION_KIND_EVENT]
            .concat(this.getEditableCalendars(this.calendars).length > 1 ? [TRANSACTION_KIND_TRANSFER] : [])
            .concat([TRANSACTION_KIND_BALANCE_UPDATE])
            .map((type) => {
                return {
                    value: type,
                    label: this.getTypeLabel(type)
                };
            });

        this.event = angular.merge({}, this.event || {});
        this.event.value = this.numberUtil.isNumeric(this.event.value)
            ? +toFixed(Math.abs(this.event.value), 2)
            : this.event.value;

        this.existing = !!this.event.id || !!this.event.templateId;
        this.canDelete = this.existing && this.canDelete;
        this.repeating = this.event.repeat;

        this.originalEntity = angular.merge({}, this.event);
        this.entities = {};
        this.selectType(this.getType(this.event));

        this.updateSummaries(this.entity);

        this.autoCategoryPromise = this.$q.resolve();
        this.debouncedGuessCategory = debounce((resolve, reject, ...args) => this.$q.when(this.guessCategory(...args)).then(resolve, reject), 250);
    }

    $postLink() {
        this.receiptInput = this.$element[0].querySelector('.input-receipt-file');

        if (this.receiptInput) {
            this.receiptInput.addEventListener('change', this.handleReceiptInputChange);
        }
    }

    $onDestroy() {
        if (this.receiptInput) {
            this.receiptInput.removeEventListener('change', this.handleReceiptInputChange);
        }
    }

    refreshCategories(updatedCategory) {
        if (!updatedCategory || !updatedCategory.id) {
            return this.categoryService.getAll(this.teamId, true).then((categories) => {
                if (!categories || categories.length === 0) {
                    return;
                }

                Object.keys(this.entities || {}).forEach((key) => {
                    const currentEntity = this.entities[key];
                    if (!currentEntity || !currentEntity.category || !currentEntity.category.id) {
                        return;
                    }

                    const latestCategory = find(categories, (category) => `${category.id}` === `${currentEntity.category.id}`);
                    if (latestCategory) {
                        currentEntity.category = latestCategory;
                    }
                });
            }).catch(() => null);
        }

        Object.keys(this.entities || {}).forEach((key) => {
            const currentEntity = this.entities[key];
            if (!currentEntity || !currentEntity.category || !currentEntity.category.id) {
                return;
            }

            if (`${currentEntity.category.id}` === `${updatedCategory.id}`) {
                currentEntity.category = angular.merge({}, currentEntity.category, updatedCategory);
            }
        });

        return null;
    }

    getEditableCalendars(calendars) {
        return filter(calendars, (calendar) => calendar && calendar.permissions.manageEvents);
    }

    getTypeLabel(type) {
        switch (type) {
            case TRANSACTION_KIND_TRANSFER:
                return 'Transfer';
            case TRANSACTION_KIND_BALANCE_UPDATE:
                return 'Balance';
        }

        return 'Transaction';
    }

    getType(entity) {
        if (entity && entity.category) {
            if (isCategoryTransfer(entity.category)) {
                return TRANSACTION_KIND_TRANSFER;
            }

            if (isCategoryBalanceUpdate(entity.category)) {
                return TRANSACTION_KIND_BALANCE_UPDATE;
            }
        }

        return TRANSACTION_KIND_EVENT;
    }

    clickSelectType(type) {
        analytics.trackTransactionTypeChange(type.label);

        this.selectType(type.value);
    }

    selectType(type) {
        this.type = type;

        this.entities[this.type] = this.entities[this.type] || this.makeEntity(this.type, this.originalEntity);

        this.isEvent = this.type === TRANSACTION_KIND_EVENT;
        this.isTransfer = this.type === TRANSACTION_KIND_TRANSFER;
        this.isBalanceUpdate = this.type === TRANSACTION_KIND_BALANCE_UPDATE;

        this.canEditCategory = this.canEdit && this.isEvent;

        this.entity = this.entities[this.type];

        this.updateSummaries(this.entity);
    }

    // eslint-disable-next-line complexity
    makeEntity(type, entity) {
        entity = angular.copy(entity);

        switch (type) {
            case TRANSACTION_KIND_TRANSFER:
                if (entity.fromCalendar) {
                    let calendar = this.findCalendarById(entity.fromCalendar);
                    this.updateFromCalendar(entity, entity.fromCalendar, calendar);
                } else if (!this.existing) {
                    let calendar = this.findCalendarById(this.calendarIds[0]);
                    this.updateFromCalendar(entity, this.calendarIds[0], calendar);
                }

                if (entity.toCalendar) {
                    let calendar = this.findCalendarById(entity.toCalendar);
                    this.updateToCalendar(entity, entity.toCalendar, calendar);
                } else if (!this.existing) {
                    let calendar = find(this.calendars, (calendar) => calendar.id !== entity.fromCalendar);
                    this.updateToCalendar(entity, calendar ? calendar.id : null, calendar);
                }

                if (!isCategoryTransfer(entity.category)) {
                    this.getCategory(isCategoryTransfer).then((category) => entity.category = category).catch(() => null);
                }

                this.updateCalendar(entity, this.findCalendarById(entity.calendarId) || this.findCalendarById(entity.fromCalendar));

                entity.type = entity.type || TRANSACTION_TYPE_EXPENSE;
                entity.isIncludedInReports = this.existing
                    ? getIncludeInReports(entity, false)
                    : false;

                break;
            case TRANSACTION_KIND_BALANCE_UPDATE:
                if (!isCategoryBalanceUpdate(entity.category)) {
                    this.getCategory(isCategoryBalanceUpdate).then((category) => entity.category = category).catch(() => null);
                }

                this.updateCalendar(entity, this.findCalendarById(entity.calendarId) || this.findCalendarById(this.calendarIds[0]));

                entity.type = entity.type || TRANSACTION_TYPE_INCOME;
                entity.transactionDate = this.timeUtil.toTransactionDate(entity.timestamp);
                entity.isIncludedInReports = this.existing
                    ? getIncludeInReports(entity, false)
                    : false;

                break;
            default:
                if (!entity.category || isCategoryTransfer(entity.category) || isCategoryBalanceUpdate(entity.category)) {
                    this.getCategory(isCategoryOther).then((category) => entity.category = category).catch(() => null);
                }

                this.updateCalendar(entity, this.findCalendarById(entity.calendarId) || this.findCalendarById(this.calendarIds[0]));

                entity.type = entity.type || TRANSACTION_TYPE_EXPENSE;
                entity.isIncludedInReports = getIncludeInReports(entity, true);

                break;
        }

        return entity;
    }

    findCalendarById(calendarId) {
        return find(this.calendars, (calendar) => calendar.id === calendarId);
    }

    updateFromCalendar(entity, calendarId, calendar) {
        entity.fromCalendar = calendarId;
        this.fromCalendar = calendar;
    }

    updateToCalendar(entity, calendarId, calendar) {
        entity.toCalendar = calendarId;
        this.toCalendar = calendar;
    }

    updateCalendar(entity, calendar) {
        entity.calendarId = calendar ? calendar.id : null;
        this.calendar = calendar;
    }

    selectFromCalendar(entity) {
        let calendars = filter(this.calendars, (calendar) => calendar.id !== entity.toCalendar && calendar.permissions.manageEvents);
        return this.openCalendarSelectionModal(calendars, this.fromCalendar, `Transfer from`).then((calendar) => {
            this.updateFromCalendar(entity, calendar.id, calendar);
            return this.updateCalendar(entity, calendar);
        }).catch(() => null);
    }

    selectToCalendar(entity) {
        let calendars = filter(this.calendars, (calendar) => calendar.id !== entity.fromCalendar && calendar.permissions.manageEvents);
        return this.openCalendarSelectionModal(calendars, this.toCalendar, `Transfer to`).then((calendar) => {
            return this.updateToCalendar(entity, calendar.id, calendar);
        }).catch(() => null);
    }

    selectCalendar(entity) {
        let calendars = filter(this.calendars, (calendar) => calendar.permissions.manageEvents);
        return this.openCalendarSelectionModal(calendars, this.calendar, `Select calendar`).then((calendar) => {
            return this.updateCalendar(entity, calendar);
        }).catch(() => null);
    }

    openCalendarSelectionModal(calendars, selected, title) {
        return this.modalService.open({
            component: 'dbACalendarSelectModal',
            bindings: {
                calendars,
                selected,
                title
            }
        });
    }

    getCategory(callback) {
        this.loading = true;

        return this.categoryService.find(this.teamId, callback).finally(() => this.loading = false);
    }

    get canNavigateBack() {
        return this.index > 0;
    }

    get income() {
        return this.entity.type && this.entity.type === TRANSACTION_TYPE_INCOME;
    }

    switchSign(entity) {
        entity.type = this.income ? TRANSACTION_TYPE_EXPENSE : TRANSACTION_TYPE_INCOME;

        analytics.trackTransactionSignChange(entity);
    }

    switchTransferCalendars(entity) {
        let fromCalendar = angular.merge({}, this.fromCalendar);
        let fromCalendarId = entity.fromCalendar;

        this.updateFromCalendar(entity, entity.toCalendar, this.toCalendar);
        this.updateToCalendar(entity, fromCalendarId, fromCalendar);
        this.updateCalendar(entity, this.fromCalendar);

        analytics.trackTransactionTransferCalendarSwitch(entity);
    }

    openCategoryModal(entity) {
        if (this.canEditCategory) {
            let previousCategory = entity.category || {};

            return this.modalService.open({
                component: 'dbACategorySelectModal',
                bindings: {
                    teamId: this.teamId,
                    selectedCategoryId: entity.category ? entity.category.id : null,
                    canAdd: this.canManageCategories
                }
            }).then((category) => {
                this.hasChangedCategory = true;

                entity.category = category;

                if (!this.existing) {
                    if (entity.autocategorized) {
                        analytics.trackTransactionAutocategoryOverride(entity, previousCategory);
                    }

                    entity.autocategorized = false;
                }
            }).catch(() => {
                return this.reloadCategory(entity).catch(() => {
                    return this.getCategory(isCategoryOther).then((category) => {
                        entity.category = category;
                    }).catch(() => null);
                });
            });
        }
    }

    reloadCategory(entity) {
        return this.getCategory((category) => category.id === entity.category.id).then((category) => {
            entity.category = category;
        });
    }

    save(entity) {
        entity.value = +toFixed(entity.value, 2);

        if (this.type === TRANSACTION_KIND_BALANCE_UPDATE) {
            return this.saveBalanceUpdate(entity);
        }

        if (this.existing && this.repeating) {
            return this.modalService.open({
                component: 'dbAEventRepeatChangeModal',
                bindings: {
                    onClose: (eventSelector) => {
                        let promise = entity.templateId
                            ? this.eventService.updateRepeating(entity, this.repeating.occurrence, eventSelector)
                            : this.eventService.update(entity, eventSelector);

                        return promise.then(this.close.bind(this)).catch((result) => {
                            let repeatEndError = getRepeatEndFieldError(result);
                            this.notificationService.showError(repeatEndError || getErrorMessage(result, `We were not able to save your transaction. Please try again later.`));
                            if (repeatEndError) {
                                return this.openDateModal(entity, repeatEndError);
                            }
                            return this.$q.reject(result);
                        });
                    }
                }
            }).catch(() => null);
        }

        return this.autoCategoryPromise.then(() => {
            let promise = null;

            if (this.existing) {
                promise = this.eventService.update(entity);
            } else if (this.type === TRANSACTION_KIND_TRANSFER) {
                promise = this.transferService.create(entity.calendarId, entity).then((entity) => {
                    analytics.trackTransactionTransferCreate(entity, this.from);
                    return entity;
                });
            } else {
                promise = this.eventService.create(entity.calendarId, entity).then((entity) => {
                    analytics.trackTransactionEventCreate(entity, this.from);

                    return entity;
                });
            }

            return promise.then(() => {
                if (entity.note && entity.note.length > 0) {
                    this.addSuggestion(entity.note);

                    if (this.isEvent) {
                        this.addCategoryMap(entity.category, entity.note);
                    }
                }

                return this.close();
            }).catch((result) => {
                let repeatEndError = getRepeatEndFieldError(result);
                this.notificationService.showError(repeatEndError || getErrorMessage(result, `We were not able to save your transaction. Please try again later.`));
                if (repeatEndError) {
                    return this.openDateModal(entity, repeatEndError);
                }
            });
        }).catch(() => null);
    }

    saveBalanceUpdate(entity) {
        let promise = this.existing
            ? this.eventService.update(entity)
            : this.balanceUpdateService.create(entity.calendarId, entity).then((entity) => {
                analytics.trackTransactionBalanceUpdateCreate(entity, this.from);
                return entity;
            });

        return promise.then(this.close.bind(this)).catch((result) => {
            this.notificationService.showError(getErrorMessage(result, `We were not able to save your balance update. Please try again later.`));
            return this.$q.reject(result);
        });
    }

    addSuggestion(note) {
        this.suggestionService.add(this.teamId, note);
    }

    addCategoryMap(category, note) {
        this.autoCategoryService.add(this.teamId, category.id, note);
    }

    remove(entity) {
        if (this.repeating) {
            return this.modalService.open({
                component: 'dbAEventRepeatDeleteModal',
                bindings: {
                    onClose: (eventSelector) => {
                        let promise = entity.templateId
                            ? this.eventService.removeRepeating(entity, this.repeating.occurrence, eventSelector)
                            : this.eventService.remove(entity, eventSelector);

                        return promise.then(() => {
                            analytics.trackTransactionDelete(entity, this.from);
                            return this.close();
                        }).catch((result) => {
                            this.notificationService.showError(getErrorMessage(result, `We were not able to save your transaction. Please try again later.`));
                            return this.$q.reject(result);
                        });
                    }
                }
            }).catch(() => null);
        }

        return this.modalService.open({
            component: 'dbAConfirmModal',
            bindings: {
                title: 'Delete transaction',
                message: [
                    `Deleting a transaction cannot be undone.`,
                    `Are you still sure about it?`
                ],
                onClose: () => this.eventService.remove(entity).then(() => {
                    analytics.trackTransactionDelete(entity, this.from);
                    return this.close();
                }).catch((result) => {
                    this.notificationService.showError(getErrorMessage(result, `We were not able to delete your transaction. Please try again later.`));
                    return this.$q.reject(result);
                })
            }
        }).catch(() => null);
    }

    toggleConfirmation(entity) {
        if (this.canEdit) {
            entity.confirmed = !entity.confirmed;
        }
    }

    openDateModal(entity, initialRepeatEndError) {
        return this.modalService.open({
            component: 'dbAEventDateModal',
            bindings: {
                event: angular.merge({}, entity),
                startOfWeek: this.startOfWeek,
                canRepeat: !this.isBalanceUpdate,
                canEdit: this.canEdit,
                initialRepeatEndError
            }
        }).then(this.updateEvent.bind(this)).catch(() => null);
    }

    openDetailsModal(entity) {
        return this.modalService.open({
            component: 'dbAEventDetailsModal',
            bindings: {
                event: angular.merge({}, entity),
                canEdit: this.canEdit
            }
        }).then(this.updateEvent.bind(this)).catch(() => null);
    }

    hasReceipt(entity) {
        return !!this.getReceiptImageUrl(entity);
    }

    getReceiptImageUrl(entity) {
        if (!entity) {
            return null;
        }

        return entity.receiptUrl || null;
    }

    handleReceiptClick(entity) {
        if (this.uploadingReceipt) {
            return null;
        }

        if (this.hasReceipt(entity)) {
            return this.openReceiptModal(entity);
        }

        if (!this.canEdit || !this.receiptInput) {
            return null;
        }

        this.receiptInput.value = '';
        this.receiptInput.click();

        return null;
    }

    handleReceiptInputChange(event) {
        return this.$q.resolve().then(() => {
            const file = event && event.target && event.target.files && event.target.files[0];

            if (!file) {
                return null;
            }

            if (file.type && file.type.indexOf('image/') !== 0) {
                return null;
            }

            const localUrl = this.$window.URL.createObjectURL(file);

            return this.modalService.open({
                component: 'dbAEventReceiptModal',
                bindings: {
                    imageUrl: localUrl,
                    fileName: file.name,
                    canAttach: true
                }
            }).then(() => {
                this.uploadingReceipt = true;

                return this.uploadReceipt(file).then((receiptUrl) => {
                    this.attachReceipt(this.entity, receiptUrl, file.name);
                }).catch((result) => {
                    this.notificationService.showError(getErrorMessage(result, `We were not able to upload your image. Please try again later.`));
                }).finally(() => {
                    this.uploadingReceipt = false;
                });
            }).catch(() => {
                return null;
            }).finally(() => {
                this.revokeLocalReceiptUrl(localUrl);
            });
        });
    }

    uploadReceipt(file) {
        // eslint-disable-next-line no-console
        console.log('[receipt-upload] selected file size (bytes):', file.size, 'name:', file.name, 'type:', file.type);
        return this.compressImageToJpeg(file).then((jpegFile) => {
            // eslint-disable-next-line no-console
            console.log('[receipt-upload] compressed jpeg size (bytes):', jpegFile.size, 'type:', jpegFile.type);
            return this.eventService.uploadReceipt(jpegFile, 'receipt.jpeg');
        }).then((url) => {
            if (!url) {
                return this.$q.reject();
            }

            return url;
        });
    }

    compressImageToJpeg(file) {
        return this.createImageElement(file).then((image) => {
            const dimensions = this.getScaledDimensions(image.naturalWidth || image.width, image.naturalHeight || image.height);
            const canvas = this.$window.document.createElement('canvas');

            canvas.width = dimensions.width;
            canvas.height = dimensions.height;

            const context = canvas.getContext('2d');
            context.drawImage(image, 0, 0, dimensions.width, dimensions.height);

            return this.$q((resolve) => {
                canvas.toBlob((blob) => resolve(blob), 'image/jpeg', RECEIPT_JPEG_QUALITY);
            }).then((blob) => {
                if (blob) {
                    return blob;
                }

                const dataUrl = canvas.toDataURL('image/jpeg', RECEIPT_JPEG_QUALITY);
                return this.dataUrlToBlob(dataUrl);
            });
        });
    }

    createImageElement(file) {
        return this.$q((resolve, reject) => {
            const image = new this.$window.Image();
            const objectUrl = this.$window.URL.createObjectURL(file);

            image.onload = () => {
                this.revokeLocalReceiptUrl(objectUrl);
                resolve(image);
            };

            image.onerror = () => {
                this.revokeLocalReceiptUrl(objectUrl);
                reject();
            };

            image.src = objectUrl;
        });
    }

    getScaledDimensions(width, height) {
        const longestEdge = Math.max(width, height);

        if (!longestEdge || longestEdge <= RECEIPT_MAX_EDGE) {
            return {
                width,
                height
            };
        }

        const ratio = RECEIPT_MAX_EDGE / longestEdge;

        return {
            width: Math.round(width * ratio),
            height: Math.round(height * ratio)
        };
    }

    dataUrlToBlob(dataUrl) {
        const parts = (dataUrl || '').split(',');

        if (parts.length < 2) {
            return this.$q.reject();
        }

        const header = parts[0];
        const content = parts[1];
        const mimeMatch = header.match(/data:(.*?);base64/);
        const mimeType = mimeMatch && mimeMatch[1] ? mimeMatch[1] : 'image/jpeg';
        const binary = this.$window.atob(content);
        const bytes = new Uint8Array(binary.length);

        for (let index = 0; index < binary.length; index++) {
            bytes[index] = binary.charCodeAt(index);
        }

        return new Blob([bytes], {
            type: mimeType
        });
    }

    attachReceipt(entity, receiptUrl, fileName) {
        if (!entity) {
            return;
        }

        entity.receiptUrl = receiptUrl;
        entity.receiptName = fileName;
    }

    openReceiptModal(entity) {
        const imageUrl = this.getReceiptImageUrl(entity);

        if (!imageUrl) {
            return null;
        }

        return this.modalService.open({
            component: 'dbAEventReceiptModal',
            bindings: {
                imageUrl,
                fileName: entity.receiptName || this.getReceiptFileName(imageUrl),
                canAttach: false
            }
        }).catch(() => null);
    }

    removeReceipt(entity) {
        if (!entity || !this.canEdit) {
            return;
        }

        entity.receiptName = null;
        entity.receiptUrl = null;
    }

    getReceiptFileName(url) {
        if (!url) {
            return 'receipt-image';
        }

        const withoutQuery = `${url}`.split('?')[0];
        const filename = withoutQuery.substring(withoutQuery.lastIndexOf('/') + 1);

        return filename || 'receipt-image';
    }

    revokeLocalReceiptUrl(url) {
        if (url && `${url}`.indexOf('blob:') === 0 && this.$window.URL && this.$window.URL.revokeObjectURL) {
            this.$window.URL.revokeObjectURL(url);
        }
    }

    updateDateSummaries(entity) {
        let timestamp = entity && (entity.transactionDate || entity.timestamp);
        let { dateSummary, repeatingSummary } = this.eventUtil.getRepeatingSummary(timestamp, entity.repeat);

        this.dateSummary = dateSummary;
        this.repeatingSummary = repeatingSummary;
    }

    updateDetailSummary(entity) {
        this.detailSummary = entity.details && entity.details.length > 0 ? entity.details.split(/\r?\n/).join(' ').trim() : null;
    }

    updateSummaries(entity) {
        this.updateDateSummaries(entity);
        this.updateDetailSummary(entity);
    }

    updateEvent(entity) {
        if (entity && typeof entity.timestamp === 'number') {
            entity.transactionDate = this.timeUtil.toTransactionDate(entity.timestamp);
        }

        this.entities[this.type] = entity;

        this.updateSummaries(entity);
        this.entity = entity;
    }

    checkKeyForNote(entity, code) {
        if (this.noteSuggestion) {
            switch (code) {
                case 27:
                    this.noteSuggestion = null;
                    return false;
                case 9:
                case 13:
                case 39:
                    this.autofillNote(entity);
                    return false;
            }
        }

        return true;
    }

    updateNote(entity) {
        if (this.canEdit) {
            let hasNote = entity.note && entity.note.length > 0;

            this.noteSuggestion = hasNote && entity.note.length < MAX_NOTE_LENGTH_FOR_AUTOCATEGORIZATION
                ? this.suggestionService.getMissingPart(this.teamId, entity.note)
                : null;

            if (this.isTransfer || this.isBalanceUpdate) {
                return null;
            }

            if (!this.existing && !this.hasChangedCategory && this.autoCategorization) {
                if (hasNote && entity.note.length > MIN_NOTE_LENGTH_FOR_AUTOCATEGORIZATION) {
                    return this.autoCategoryPromise = this.$q((resolve) => this.debouncedGuessCategory(resolve, resolve, this.teamId, entity, entity.note));
                }

                return this.getCategory(isCategoryOther).then((category) => entity.category = category).catch(() => null);
            }
        }
    }

    guessCategory(teamId, entity, note) {
        if (note && note.length > 2) {
            return this.autoCategoryService.get(teamId, note).then((categoryId) => {
                return this.categoryService.getAll(teamId).then((categories) => {
                    let category = find(categories, (category) => `${category.id}` === `${categoryId}`);

                    if (category) {
                        entity.category = category;

                        return category;
                    }

                    return this.$q.reject();
                });
            }).catch(() => {
                return this.autoCategoryService.guess(teamId, note).then((category) => {
                    entity.category = category;

                    if (!this.existing) {
                        entity.autocategorized = true;
                    }
                }).catch(() => {
                    return this.getCategory(isCategoryOther).then((category) => entity.category = category).catch(() => null);
                });
            });
        }

        return this.getCategory(isCategoryOther).then((category) => entity.category = category).catch(() => null);
    }

    autofillNote(entity) {
        entity.note = this.suggestionService.get(this.teamId, entity.note) || entity.note;
        this.noteSuggestion = null;

        this.updateNote(entity);

        analytics.trackTransactionLabelSuggestionAutocomplete(entity);
    }

    close() {
        return this.onClose();
    }

    dismiss() {
        return this.onDismiss();
    }
}

export default {
    selector: 'dbAEventEditModal',
    template: template(),
    controller: EventEditController,
    bindings: {
        teamId: '<',
        calendarIds: '<',
        calendars: '<',
        event: '<',
        startOfWeek: '<',
        autoCategorization: '<',
        from: '<',
        index: '<',

        canEdit: '<',
        canDelete: '<',
        canManageCategories: '<',

        onClose: '&',
        onDismiss: '&'
    }
};



// WEBPACK FOOTER //
// ./src/application/component/modals/component/event-edit-modal/event-edit-modal.cmpt.js